import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Button,
	Header,
	Icon,
	IconProps,
	SpaceBetween
} from '@cloudscape-design/components'
import { TagsApproval } from '../../../types/tags'
import styles from './index.module.css'

export type ApprovalStatus =
	| 'APPROVED'
	| 'REJECTED'
	| 'PENDING'

export interface Approval {
	label: string
	status: ApprovalStatus
}

// Normalize various incoming approval shapes
// to Approval with ApprovalStatus
const normalizeApproval = (
	input: any
): Approval => {
	const label =
		input?.label || input?.name || ''

	const rawStatus = input?.status

	let status: ApprovalStatus = 'PENDING'

	if (
		rawStatus === 1 ||
		rawStatus === '1' ||
		String(rawStatus).toLowerCase() ===
			'true' ||
		String(rawStatus).toUpperCase() ===
			'APPROVED'
	) {
		status = 'APPROVED'
	} else if (
		rawStatus === 0 ||
		rawStatus === '0' ||
		String(rawStatus).toLowerCase() ===
			'false' ||
		String(rawStatus).toUpperCase() ===
			'REJECTED'
	) {
		status = 'REJECTED'
	} else if (
		rawStatus &&
		typeof rawStatus === 'string' &&
		rawStatus.toUpperCase() ===
			'PENDING'
	) {
		status = 'PENDING'
	}

	return {
		label: String(label),
		status
	}
}

// Map page-level status strings to ApprovalStatus
export const mapPageStatusToApprovalStatus = (
	status?: string
): ApprovalStatus | undefined => {
	switch (status) {
		case 'PENDING_APPROVAL':
			return 'PENDING'

		case 'APPROVED':
			return 'APPROVED'

		case 'REJECTED':
			return 'REJECTED'

		default:
			return undefined
	}
}

// Helpers to inspect approvals
export const isAnyApprovalApproved = (
	approvals?: Approval[]
): boolean =>
	!!approvals &&
	approvals.some(
		(a) => a.status === 'APPROVED'
	)

export const isAllApprovalsApproved = (
	approvals?: Approval[]
): boolean =>
	!!approvals &&
	approvals.length > 0 &&
	approvals.every(
		(a) => a.status === 'APPROVED'
	)

// Convert radio input state into Approval[]
export const buildApprovalsFromRadios = (
	radios: {
		platformOwner?: string
		finance?: string
		cloudEco?: string
		cloudTribe?: string
	}
): Approval[] => {
	const toStatus = (
		value?: string
	): ApprovalStatus => {
		if (!value) {
			return 'PENDING'
		}

		const normalizedValue =
			value.toUpperCase()

		if (
			normalizedValue ===
				'APPROVED' ||
			normalizedValue ===
				'REJECTED' ||
			normalizedValue ===
				'PENDING'
		) {
			return normalizedValue as ApprovalStatus
		}

		if (
			normalizedValue ===
			'APPROVE'
		) {
			return 'APPROVED'
		}

		return 'PENDING'
	}

	return [
		{
			label: 'Platform Owner',
			status: toStatus(
				radios.platformOwner
			)
		},
		{
			label: 'Finance',
			status: toStatus(
				radios.finance
			)
		},
		{
			label: 'Cloud Eco',
			status: toStatus(
				radios.cloudEco
			)
		},
		{
			label: 'Cloud Tribe',
			status: toStatus(
				radios.cloudTribe
			)
		}
	]
}

// Chip colors + icons per approval state
const CHIP_STYLES: Record<
	ApprovalStatus,
	{
		background: string
		border: string
		color: string
		icon: IconProps.Name
		text: string
	}
> = {
	APPROVED: {
		background:
			'rgba(34, 197, 94, 0.14)',
		border: '#22C55E',
		color: '#86EFAC',
		icon: 'status-positive',
		text: 'Approved'
	},

	REJECTED: {
		background:
			'rgba(239, 68, 68, 0.14)',
		border: '#EF4444',
		color: '#FCA5A5',
		icon: 'status-negative',
		text: 'Rejected'
	},

	PENDING: {
		background:
			'rgba(59, 130, 246, 0.16)',
		border: '#3B82F6',
		color: '#93C5FD',
		icon: 'status-in-progress',
		text: 'Pending'
	}
}

// DESIGN C:
// approver name on top,
// status word below
const getApprovalBadges = (
	approvals: Approval[]
) => {
	const normalized =
		approvals.map(normalizeApproval)

	return (
		<SpaceBetween
			direction="horizontal"
			size="s"
		>
			{normalized.map(
				({ label, status }) => {
					const {
						background,
						border,
						color,
						icon,
						text
					} =
						CHIP_STYLES[
							status
						]

					return (
						<span
							key={label}
							style={{
								display:
									'inline-flex',
								alignItems:
									'center',
								gap: 8,
								padding:
									'6px 12px 6px 10px',
								borderRadius: 8,
								border:
									`1px solid ${border}`,
								background,
								color
							}}
						>
							<span
								style={{
									display:
										'inline-flex',
									color
								}}
							>
								<Icon
									name={icon}
								/>
							</span>

							<span
								style={{
									display:
										'flex',
									flexDirection:
										'column',
									lineHeight:
										1.25
								}}
							>
								<span
									style={{
										fontSize: 13,
										fontWeight:
											500
									}}
								>
									{label}
								</span>

								<span
									style={{
										fontSize: 11,
										opacity:
											0.85
									}}
								>
									{text}
								</span>
							</span>
						</span>
					)
				}
			)}
		</SpaceBetween>
	)
}

// Ensure we always render the four approver chips
// in fixed order
const DEFAULT_APPROVER_LABELS = [
	'Platform Owner',
	'Finance',
	'Cloud Economics',
	'Cloud Tribe'
]

const ensureFourApprovals = (
	source?: Approval[]
): Approval[] => {
	const mapByLabel =
		new Map<string, Approval>()

	source?.forEach((approval) => {
		mapByLabel.set(
			approval.label,
			approval
		)
	})

	const hasCustomLabels =
		source?.some(
			(approval) =>
				!DEFAULT_APPROVER_LABELS.includes(
					approval.label
				)
		) ?? false

	if (hasCustomLabels) {
		return source ?? []
	}

	return DEFAULT_APPROVER_LABELS.map(
		(label) => {
			const found =
				mapByLabel.get(label)

			if (found) {
				return found
			}

			return {
				label,
				status: 'PENDING'
			}
		}
	)
}

const sortApprovalsForDisplay = (
	approvals: Approval[]
): Approval[] => {
	const priority: Record<string, number> = {
		'Platform Owner': 0,
		Finance: 1,
		'Cloud Economics': 2,
		'ISDP Approver': 3,
		'Application Owner': 4,
		ITAMTeam: 5,
		'Cloud Tribe': Number.MAX_SAFE_INTEGER
	}

	return [...approvals].sort((a, b) => {
		const aPriority =
			priority[a.label] ?? 99
		const bPriority =
			priority[b.label] ?? 99

		return aPriority - bPriority
	})
}

const getRequestStatusApproval = (
	status?: string
): Approval => ({
	label: 'Approval Status',
	status:
		mapPageStatusToApprovalStatus(
			status
		) ?? 'PENDING'
})

interface HeaderWithStatusBadgesProps {
	headerName: string
	status?: string
	approvals?: Approval[]
	approvalStatus?: Array<TagsApproval>
	includeCloudTribe?: boolean
	isLoadingApprovals?: boolean
	skeletonBadgeCount?: number
	navigateTo?: string
}

const mapTagsApprovalToHeaderApprovals = (
	approvalStatus: Array<TagsApproval> = [],
	includeCloudTribe = false
): Approval[] => {
	const toApprovalStatus = (
		rawStatus: unknown
	): ApprovalStatus => {
		if (
			rawStatus === 1 ||
			rawStatus === '1' ||
			String(rawStatus).toLowerCase() ===
				'true' ||
			String(rawStatus).toUpperCase() ===
				'APPROVED'
		) {
			return 'APPROVED'
		}

		if (
			rawStatus === 0 ||
			rawStatus === '0' ||
			String(rawStatus).toLowerCase() ===
				'false' ||
			String(rawStatus).toUpperCase() ===
				'REJECTED'
		) {
			return 'REJECTED'
		}

		return 'PENDING'
	}

	const getLabelFromName = (
		name: string = ''
	) => {
		const normalized =
			name.toLowerCase()

		if (
			normalized.includes(
				'platformowner'
			)
		) {
			return 'Platform Owner'
		}

		if (
			normalized.includes(
				'applicationowner'
			) ||
			normalized.includes(
				'application owner'
			)
		) {
			return 'Application Owner'
		}

		if (
			normalized.includes(
				'financeteam'
			) ||
			normalized.includes(
				'finance'
			)
		) {
			return 'Finance'
		}

		if (
			normalized.includes(
				'cloudeconomics'
			) ||
			normalized.includes(
				'cloudeco'
			) ||
			normalized.includes(
				'cloud eco'
			) ||
			normalized.includes(
				'cloud economics'
			) ||
			normalized.includes('eco')
		) {
			return 'Cloud Economics'
		}

		if (
			normalized.includes(
				'cloudtribe'
			) ||
			normalized.includes(
				'tribe'
			)
		) {
			return 'Cloud Tribe'
		}

		if (
			normalized.includes(
				'isdp'
			)
		) {
			return 'ISDP Approver'
		}

		return name
	}

	const visibleLabels =
		Array.from(
			new Set(
				approvalStatus
					.map((approval) =>
						getLabelFromName(
							approval?.name || ''
						)
					)
					.filter(Boolean)
			)
		)

	const targetLabels =
		visibleLabels.some(
			(label) =>
				!DEFAULT_APPROVER_LABELS.includes(
					label
				)
		)
			? visibleLabels
			: DEFAULT_APPROVER_LABELS
	if (includeCloudTribe && !targetLabels.includes('Cloud Tribe')) {
		targetLabels.push('Cloud Tribe')
	}

	return targetLabels.map((label) => {
		const match = approvalStatus.find(
			(approval) => {
				const approvalName =
					approval?.name || ''

				const resolvedLabel =
					getLabelFromName(
						approvalName
					)

				return (
					resolvedLabel === label ||
					resolvedLabel
						.toLowerCase()
						.replace(/\s+/g, '') ===
						label
							.toLowerCase()
							.replace(/\s+/g, '')
				)
			}
		)

		const statusKey: ApprovalStatus =
			match
				? toApprovalStatus(match.status)
				: 'PENDING'

		return {
			label,
			status: statusKey
		}
	})
}

const HeaderWithStatusBadges: FC<
	HeaderWithStatusBadgesProps
> = ({
	headerName,
	status,
	approvals,
	approvalStatus,
	includeCloudTribe = false,
	isLoadingApprovals = false,
	skeletonBadgeCount = 1,
	navigateTo
}) => {
	const navigate = useNavigate()

	/*
	 * Keep the last valid approval statuses.
	 *
	 * If the API request uses getTags=false,
	 * approvalStatus can be empty.
	 *
	 * We must NOT replace the previous
	 * statuses with Pending.
	 */
	const [
		liveApprovals,
		setLiveApprovals
	] = useState<Approval[] | undefined>(
		() => {
			if (
				approvalStatus &&
				(approvalStatus.length > 0 || includeCloudTribe)
			) {
				return mapTagsApprovalToHeaderApprovals(
					approvalStatus,
					includeCloudTribe
				)
			}

			return approvals
		}
	)

	/*
	 * Update the header only when actual
	 * approval tag data is available.
	 *
	 * approvalStatus = [] means there are
	 * currently no tag approvals in the response.
	 *
	 * DO NOT reset the existing badges.
	 */
	useEffect(() => {
		if (
			approvalStatus &&
			(approvalStatus.length > 0 || includeCloudTribe)
		) {
			console.log(
				'Header: updating approval statuses:',
				approvalStatus
			)

			setLiveApprovals(
				mapTagsApprovalToHeaderApprovals(
					approvalStatus,
					includeCloudTribe
				)
			)

			return
		}

		/*
		 * Only use approvals if approvalStatus
		 * is completely undefined.
		 */
		if (
			approvalStatus === undefined &&
			approvals
		) {
			setLiveApprovals(approvals)
		}

		/*
		 * IMPORTANT:
		 *
		 * If approvalStatus is []:
		 * do nothing.
		 *
		 * This preserves the previous
		 * liveApprovals state.
		 */
	}, [
		approvalStatus,
		approvals,
		includeCloudTribe
	])

	/*
	 * Listen for approval updates from
	 * ApproverOverview.
	 */
	useEffect(() => {
		const handleApprovalChange = (
			event: Event
		) => {
			const detail = (
				event as CustomEvent
			).detail

			/*
			 * Ignore empty updates.
			 *
			 * This prevents an empty approval
			 * response from resetting all
			 * badges to Pending.
			 */
			if (
				Array.isArray(detail) &&
				detail.length > 0
			) {
				console.log(
					'Header: approvalStatusUpdated:',
					detail
				)

				setLiveApprovals(
					mapTagsApprovalToHeaderApprovals(
						detail,
						includeCloudTribe
					)
				)
			}
		}

		window.addEventListener(
			'approvalStatusUpdated',
			handleApprovalChange
		)

		return () => {
			window.removeEventListener(
				'approvalStatusUpdated',
				handleApprovalChange
			)
		}
	}, [includeCloudTribe])

	/*
	 * IMPORTANT:
	 *
	 * Do not calculate the badges directly
	 * from approvalStatus here.
	 *
	 * liveApprovals contains the last
	 * valid approval state.
	 */
	const resolvedApprovals =
		liveApprovals ??
		approvals ??
		[]

	const shouldShowApproverBadges =
		resolvedApprovals.length > 1

	const finalApprovals =
		!shouldShowApproverBadges
			? [
					getRequestStatusApproval(
						status
					)
			  ]
			: sortApprovalsForDisplay(
					ensureFourApprovals(
						resolvedApprovals
					).map((approval) => {
						const pageStatus =
							mapPageStatusToApprovalStatus(
								status
							)

						/*
						 * Preserve the existing special
						 * behavior for Cloud Tribe when
						 * the overall request has a decisive
						 * page-level status.
						 */
						if (
							approval.label.toLowerCase() ===
								'cloud tribe' &&
							pageStatus &&
							pageStatus !== 'PENDING'
						) {
							return {
								...approval,
								status: pageStatus
							}
						}

						return approval
					})
			  )

	return (
		<Header>
			<SpaceBetween
				direction="horizontal"
				size="l"
			>
				<Button
					variant="icon"
					iconName="arrow-left"
					onClick={() =>
						navigateTo
							? navigate(
									navigateTo
								)
							: navigate(-1)
					}
					key="icon"
				/>

				<div key="text">
					{headerName}
				</div>
			</SpaceBetween>

			<div
				style={{
					marginTop: '8px',
					marginLeft: '40px'
				}}
			>
				{isLoadingApprovals ? (
					<div
						className={styles.approvalBadgeSkeletons}
						role="status"
						aria-label="Loading approval statuses"
					>
						{Array.from({ length: skeletonBadgeCount }, (_, item) => (
							<span
								className={styles.approvalBadgeSkeleton}
								aria-hidden="true"
								key={item}
							/>
						))}
					</div>
				) : (
					getApprovalBadges(finalApprovals)
				)}
			</div>
		</Header>
	)
}

export default HeaderWithStatusBadges
 